import React, {useMemo, useState} from 'react';
import styled from "styled-components";
import {FilterSchema} from "../../287/tables/UsersTable";
import {ActionMeta, default as Select, GroupBase, OptionsOrGroups, StylesConfig} from "react-select";
import chroma from 'chroma-js';
import {useAppDispatch} from "../../hooks";
import {GroupSelectOption, SelectOption} from "../../Types";
import {setFilterValue} from "../../features/tableSlice";
import {SELECT_BACK_COLOR} from '../CONSTANTS';


export const selectMapColourStyles: StylesConfig<any, true> = {
    control: (styles) => ({...styles, backgroundColor: SELECT_BACK_COLOR, borderRadius: '0', borderWidth: '0'}),
    menu: (styles) => ({...styles, backgroundColor: SELECT_BACK_COLOR, borderRadius: '0', marginTop: '0'}),
    input: (styles) => ({...styles, color: 'white'}),
    singleValue: (styles) => ({...styles, color: 'white'}),
    option: (styles, {data, isDisabled, isFocused, isSelected}) => {
        const color = chroma(data.color || "#fff");
        return {
            ...styles,
            backgroundColor: isDisabled
                ? undefined
                : isSelected
                    ? (data.color || "#fff")
                    : isFocused
                        ? color.alpha(0.1).css()
                        : SELECT_BACK_COLOR,
            color: isDisabled
                ? '#ccc'
                : isSelected
                    ? chroma.contrast(color, 'white') > 2
                        ? 'white'
                        : 'black'
                    : (data.color || "#fff"),
            cursor: isDisabled ? 'not-allowed' : 'default',

            ':active': {
                ...styles[':active'],
                backgroundColor: !isDisabled
                    ? isSelected
                        ? (data.color || "#fff")
                        : color.alpha(0.3).css()
                    : undefined,
            },
        };
    },
    multiValue: (styles, {data}) => {
        const color = chroma(data.color || "#fff");
        return {
            ...styles,
            backgroundColor: color.alpha(0.1).css(),
            maxWidth: '300px'
        };
    },
    multiValueLabel: (styles, {data}) => ({
        ...styles,
        color: (data.color || "#fff"),
    }),
    multiValueRemove: (styles, {data}) => ({
        ...styles,
        color: (data.color || "#fff"),
        ':hover': {
            backgroundColor: (data.color || "#fff"),
            color: 'white',
        },
    }),
};

const lang = 'ua'

function iniState(schema: FilterSchema, values: any[]) {
    const options = [] as any[]
    values && schema.mapSchema?.options?.forEach(o => {
        if (values.includes(o.value))
            options.push({
                ...o,
                label: o.label[lang],
            })
    });
    return options
}

function FilterMultiMapValue({index, schema, initialValue, textCenter = false}: {
    index: number,
    schema: FilterSchema,
    initialValue: any,
    textCenter?: boolean
}) {
    const dispatch = useAppDispatch();
    let [value, setValue] = useState<OptionsOrGroups<SelectOption, GroupBase<SelectOption>>>([...iniState(schema, initialValue)]);

    const options = useMemo(() =>
        ((schema.mapSchema?.groups?.map(o => ({
                label: o.label[lang],
                options: o.options.map(opt => ({...opt, label: opt.label[lang]}))
            })) ||
            schema.mapSchema?.options?.map(o => ({
                ...o,
                label: o.label[lang],
            }))) as OptionsOrGroups<GroupSelectOption, GroupBase<GroupSelectOption>> | undefined), [schema.mapSchema?.groups, schema.mapSchema?.options]);

    function onChange(newValue: any, actionMeta: ActionMeta<unknown>) {
        const value = newValue as SelectOption[]
        setValue(value)
        dispatch(setFilterValue({index, value: value.length ? value.map(x => x.value) : []}));
    }

    return <Container>
        <Select
            className={'select-map'}
            value={value}
            onChange={onChange}
            closeMenuOnSelect={false}
            isMulti
            noOptionsMessage={() => 'Усі варіанти обрано'}
            placeholder={"Оберіть значення"}
            options={options}
            styles={selectMapColourStyles}
        />
    </Container>
}

export default FilterMultiMapValue;


const Container = styled.div`

    width: 100%;
    
    .select-map {
        border-radius: 0;
        color: black;
    }

    & .wrapper {
        display: flex;
        gap: .5rem;
    }


`

export type OptionType = {
    label: string;
    value: string;
    description: string;
    color?: string;
};

export  const  selectMapColourStylesSingle: StylesConfig<any, false> = {
    control: (styles) => ({
        ...styles,
        backgroundColor: SELECT_BACK_COLOR,
        borderRadius: '0',
        borderWidth: '0',
    }),
    menu: (styles) => ({
        ...styles,
        backgroundColor: SELECT_BACK_COLOR,
        borderRadius: '0',
        marginTop: '0',
    }),
    input: (styles) => ({
        ...styles,
        color: 'white',
    }),
    singleValue: (styles, { data }) => {
        const bgColor = data.color || "#fff";
        const textColor = getReadableTextColor(bgColor);
        return {
            ...styles,
            backgroundColor: bgColor,
            color: textColor,
            padding: '2px 8px',
            borderRadius: '3px',
        };
    },
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
        const color = chroma(data.color || "#fff");
        return {
            ...styles,
            backgroundColor: isDisabled
                ? undefined
                : isSelected
                    ? (data.color || "#fff")
                    : isFocused
                        ? color.alpha(0.1).css()
                        : SELECT_BACK_COLOR,
            color: isDisabled
                ? '#ccc'
                : isSelected
                    ? chroma.contrast(color, 'white') > 2 ? 'white' : 'black'
                    : (data.color || "#fff"),
            cursor: isDisabled ? 'not-allowed' : 'default',
            ':active': {
                ...styles[':active'],
                backgroundColor: !isDisabled
                    ? isSelected
                        ? (data.color || "#fff")
                        : color.alpha(0.3).css()
                    : undefined,
            },
        };
    },
};

export function getReadableTextColor(bgColor: string) {
    const whiteContrast = chroma.contrast(bgColor, "white");
    const blackContrast = chroma.contrast(bgColor, "black");

    // Threshold for "good enough" contrast per WCAG
    const minContrast = 4.5;

    if (whiteContrast >= minContrast) return "white";
    if (blackContrast >= minContrast) return "black";

    // If both contrasts are too low, fallback to semi-transparent black or white for readability
    return whiteContrast > blackContrast
        ? "rgba(255, 255, 255, 0.85)" // mostly white, but transparent
        : "rgba(0, 0, 0, 0.85)";      // mostly black, but transparent
}
